export const DEFAULT_PRICE_RECOMMENDATION_SETTINGS = {
  autoUpdatePrices: true,
  rounding: {
    mode: 'hundreds_threshold',
    threshold: 20,
  },
};

const STOCK_PRICE_RULE_MODES = ['markup_amount', 'markup_percent', 'fixed_price'];

export const parsePriceNumber = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const normalizePriceRecommendationSettings = (settings) => {
  const threshold = parsePriceNumber(settings?.rounding?.threshold);
  return {
    autoUpdatePrices: settings?.autoUpdatePrices === undefined
      ? DEFAULT_PRICE_RECOMMENDATION_SETTINGS.autoUpdatePrices
      : settings.autoUpdatePrices !== false,
    rounding: {
      mode: 'hundreds_threshold',
      threshold: threshold === null
        ? DEFAULT_PRICE_RECOMMENDATION_SETTINGS.rounding.threshold
        : Math.min(99, Math.max(0, Math.round(threshold))),
    },
  };
};

export const roundHundredsThreshold = (value, threshold = 20) => {
  const parsed = parsePriceNumber(value);
  if (parsed === null) return null;
  const base = Math.max(0, Math.round(parsed));
  const normalizedThreshold = Math.min(99, Math.max(0, Math.round(parsePriceNumber(threshold) ?? 20)));
  const remainder = base % 100;
  if (remainder === 0) return base;
  if (remainder <= normalizedThreshold) return base - remainder;
  return base + (100 - remainder);
};

export const calculateStockRulePrice = ({ rule, referenceCost, settings }) => {
  if (!rule || typeof rule !== 'object') {
    return { error: 'missing_stock_price_rule' };
  }
  const mode = String(rule.mode || '').trim().toLowerCase();
  if (!STOCK_PRICE_RULE_MODES.includes(mode)) {
    return { error: 'invalid_stock_price_rule' };
  }
  const value = parsePriceNumber(rule.value);
  if (value === null || value < 0) {
    return { error: 'invalid_stock_price_rule' };
  }

  const cost = parsePriceNumber(referenceCost);
  if (cost === null) {
    return { error: 'missing_reference_cost' };
  }

  let rawPrice = null;
  if (mode === 'markup_amount') {
    rawPrice = cost + Math.round(value);
  } else if (mode === 'markup_percent') {
    rawPrice = cost * (1 + value / 100);
  } else if (mode === 'fixed_price') {
    rawPrice = Math.round(value);
  }

  if (rawPrice === null) {
    return { error: 'invalid_stock_price_rule' };
  }

  const normalizedSettings = normalizePriceRecommendationSettings(settings);
  const price = mode === 'fixed_price'
    ? Math.max(0, Math.round(rawPrice))
    : roundHundredsThreshold(rawPrice, normalizedSettings.rounding.threshold);

  return {
    rule: {
      mode,
      value: mode === 'markup_percent' ? value : Math.round(value),
    },
    rawPrice,
    price,
  };
};
